import React from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  User,
  Clock,
  Flag,
  TrendingUp,
  ShieldAlert,
  AlertOctagon,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import { LoadingSpinner } from '../../ui/Loading';
import { WithdrawalRequest } from '../../../types';
import { useWithdrawalProfitAnalysis } from '../../../hooks/useApi';
import { cn, formatCurrency } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';

interface ProfitAnalysisPanelProps {
  withdrawal: WithdrawalRequest;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onAdjustBalance: () => void;
}

function formatSignedCurrency(amount: number): string {
  const abs = formatCurrency(Math.abs(amount));
  if (amount < 0) return `-${abs}`;
  if (amount > 0) return `+${abs}`;
  return abs;
}

function riskScoreColor(score: number): string {
  if (score >= 70) return 'text-red-700';
  if (score >= 40) return 'text-orange-700';
  return 'text-amber-700';
}

function riskScoreBg(score: number): string {
  if (score >= 70) return 'border-red-300 bg-red-50';
  if (score >= 40) return 'border-orange-300 bg-orange-50';
  return 'border-amber-300 bg-amber-50';
}

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  subtext?: React.ReactNode;
  triggered: boolean;
  icon: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  subtext,
  triggered,
  icon,
}) => (
  <div
    className={cn(
      'rounded-lg p-4 border-2',
      triggered
        ? 'border-red-300 bg-red-50'
        : 'border-gray-200 bg-gray-50/80'
    )}
  >
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-sm font-medium">{label}</span>
      {triggered && (
        <Badge className="bg-red-600 text-white border-red-700 text-[10px] ml-auto">
          EXCEEDED
        </Badge>
      )}
    </div>
    <div
      className={cn(
        'text-2xl font-bold',
        triggered ? 'text-red-700' : 'text-gray-800'
      )}
    >
      {value}
    </div>
    {subtext && (
      <div className="text-xs text-muted-foreground mt-1">{subtext}</div>
    )}
  </div>
);

function EntityLink({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={cn('text-primary hover:underline', className)}>
      {children}
    </Link>
  );
}

function formatProfitAnalysisError(error: unknown): { message: string; status?: number } {
  const err = error as {
    message?: string;
    response?: { status?: number; data?: { message?: string; message_localized?: string } };
  };
  const status = err.response?.status;
  const apiMessage =
    err.response?.data?.message_localized ||
    err.response?.data?.message ||
    err.message ||
    'Unknown error';
  return { message: apiMessage, status };
}

const ProfitAnalysisPanel: React.FC<ProfitAnalysisPanelProps> = ({
  withdrawal,
  onClose,
  onApprove,
  onReject,
  onAdjustBalance,
}) => {
  const fmt = useFormatDate();
  const { data: analysisData, isLoading, error } = useWithdrawalProfitAnalysis(
    withdrawal.transactionId
  );

  const analysis = analysisData?.data;
  const profitAnalysisError = error ? formatProfitAnalysisError(error) : null;
  const hasAnalysisPayload = Boolean(analysis?.profitAnalysis);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getFlagTypeLabel = (flagType: string) => {
    const map: Record<string, string> = {
      abnormal_profit_ratio: 'Abnormal Profit Ratio',
      abnormal_absolute_profit: 'Abnormal Absolute Profit',
      new_account_high_profit: 'New Account High Profit',
      repeated_high_profit: 'Repeated High Profit',
      rapid_deposit_withdrawal: 'Rapid Deposit-Withdrawal',
      high_deposit_volume: 'High Deposit Volume',
      high_deposit_frequency: 'High Deposit Frequency',
    };
    return map[flagType] || flagType;
  };

  const profit = analysis?.profitAnalysis;
  const alert = analysis?.alertSummary;
  const relatedFlags =
    analysis?.relatedFlags ?? analysis?.previousFlagCount ?? 0;

  const subjectType = analysis?.subjectType ?? 'player';
  const isPlayerSubject = subjectType === 'player';
  const showProfitMetrics = analysis?.applicable !== false;
  const isCreditLedger = profit?.ledgerSource === 'hierarchy_cash';
  const amountLabel = isCreditLedger ? 'credits' : undefined;
  const formatLedgerAmount = (amount: number, signed = false) => {
    if (isCreditLedger) {
      const abs = Math.abs(amount).toLocaleString();
      if (signed && amount < 0) return `-${abs} credits`;
      if (signed && amount > 0) return `+${abs} credits`;
      return `${abs} credits`;
    }
    return signed ? formatSignedCurrency(amount) : formatCurrency(amount);
  };
  const ownerLinkPath =
    subjectType === 'shop'
      ? `/shops/${encodeURIComponent(analysis?.user?.id ?? '')}`
      : subjectType === 'agent'
        ? `/agents/${encodeURIComponent(analysis?.user?.id ?? '')}`
        : subjectType === 'super_agent'
          ? `/super-agents/${encodeURIComponent(analysis?.user?.id ?? '')}`
          : analysis?.user?.id
            ? `/users/${encodeURIComponent(analysis.user.id)}`
            : null;
  const ownerInfoLabel =
    subjectType === 'shop'
      ? 'Shop'
      : subjectType === 'agent'
        ? 'Agent'
        : subjectType === 'super_agent'
          ? 'Super Agent'
          : 'User';

  const opsReviewMessages =
    analysis?.opsReviewMessages?.length
      ? analysis.opsReviewMessages
      : alert?.opsReviewMessages?.length
        ? alert.opsReviewMessages
        : [];

  const fraudAlertMessages =
    analysis?.fraudAlertMessages?.length
      ? analysis.fraudAlertMessages
      : alert?.fraudAlertMessages?.length
        ? alert.fraudAlertMessages
        : [];

  const legacyMessages =
    analysis?.flagReasonMessages?.length
      ? analysis.flagReasonMessages
      : alert?.flagReasonMessages ?? [];

  const hasFraudSignals =
    analysis?.hasFraudSignals ??
    alert?.hasFraudSignals ??
    fraudAlertMessages.length > 0;

  const opsReviewOnly =
    analysis?.opsReviewOnly ??
    alert?.opsReviewOnly ??
    (opsReviewMessages.length > 0 && !hasFraudSignals);

  const resolvedOpsMessages =
    opsReviewMessages.length > 0
      ? opsReviewMessages
      : opsReviewOnly || profit?.withdrawalThresholdExceeded
        ? legacyMessages.filter((m) =>
            /exceeded .* (auto-approve limit|threshold)/i.test(m)
          )
        : [];

  const resolvedFraudMessages =
    fraudAlertMessages.length > 0
      ? fraudAlertMessages
      : legacyMessages.filter(
          (m) => !resolvedOpsMessages.includes(m)
        );

  const fraudRiskScore =
    analysis?.fraudRiskScore ??
    alert?.fraudRiskScore ??
    (hasFraudSignals ? analysis?.riskScore ?? alert?.riskScore ?? 0 : 0);

  const playerPath = isPlayerSubject ? ownerLinkPath : null;
  const orgOwnerPath = !isPlayerSubject ? ownerLinkPath : null;
  const shop = analysis?.user?.shop ?? null;
  const agent = analysis?.user?.agent ?? null;
  const superAgent = analysis?.user?.superAgent ?? null;
  const showHierarchy =
    shop ||
    agent ||
    superAgent ||
    analysis?.shopName ||
    analysis?.agentName ||
    analysis?.superAgentName;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 sticky top-0 bg-card z-10 border-b">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-lg font-semibold">
              {opsReviewOnly ? 'Withdrawal Review' : 'Withdrawal Review & Fraud Signals'}
            </CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="pt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-muted-foreground">Loading analysis...</span>
            </div>
          ) : error || !hasAnalysisPayload ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-8 w-8 mx-auto text-destructive mb-2" />
              <p className="text-destructive">Failed to load profit analysis</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                {profitAnalysisError?.message ||
                  (analysisData && !hasAnalysisPayload
                    ? 'API returned success but no analysis payload (missing data.profitAnalysis).'
                    : 'This withdrawal may not have profit analysis data.')}
              </p>
              {profitAnalysisError?.status != null && (
                <p className="text-xs text-muted-foreground mt-2">
                  HTTP {profitAnalysisError.status}
                  {withdrawal.transactionId
                    ? ` · ${withdrawal.transactionId}`
                    : ''}
                </p>
              )}
            </div>
          ) : analysis && profit ? (
            <div className="space-y-6">
              {!showProfitMetrics && (
                <div className="rounded-lg border-2 border-amber-200 bg-amber-50/80 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-amber-800" />
                    <span className="text-sm font-semibold uppercase tracking-wide text-amber-950">
                      Organization withdrawal review
                    </span>
                  </div>
                  <p className="text-sm text-amber-950">
                    {profit.skipReason ||
                      'Organization ledger analysis is not available for this withdrawal type.'}
                  </p>
                </div>
              )}

              {showProfitMetrics && profit.ledgerLabel && !isPlayerSubject && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm text-blue-950">
                  Ledger source: <span className="font-medium">{profit.ledgerLabel}</span>
                  {profit.skipped && profit.skipReason ? (
                    <span className="block mt-1 text-blue-900">{profit.skipReason}</span>
                  ) : null}
                </div>
              )}

              {/* Manual review (ops) — amount threshold, not fraud */}
              {resolvedOpsMessages.length > 0 && (
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50/80 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-blue-900">
                      Manual Review Required
                    </span>
                    <Badge className="bg-blue-100 text-blue-900 border-blue-300 text-[10px]">
                      OPS — NOT FRAUD SCORE
                    </Badge>
                  </div>
                  <p className="text-sm font-medium text-blue-950 mb-2">
                    Requires review because:
                  </p>
                  <ul className="space-y-1.5">
                    {resolvedOpsMessages.map((msg, i) => (
                      <li
                        key={i}
                        className="text-sm text-blue-950 flex items-start gap-2"
                      >
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span>{msg}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-blue-800/90 mt-3">
                    This is an operational approval rule (withdrawal amount vs auto-approve limit).
                    It does not increase fraud risk score or prior profit fraud flags.
                  </p>
                </div>
              )}

              {/* Fraud signals */}
              {hasFraudSignals && resolvedFraudMessages.length > 0 && (
                <div
                  className={cn(
                    'rounded-lg border-2 p-4',
                    riskScoreBg(fraudRiskScore)
                  )}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                      Fraud Signals
                    </span>
                    <span
                      className={cn('text-lg font-bold', riskScoreColor(fraudRiskScore))}
                    >
                      Fraud Risk Score: {fraudRiskScore}/100
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    Flagged because:
                  </p>
                  <ul className="space-y-1.5">
                    {resolvedFraudMessages.map((msg, i) => (
                      <li
                        key={i}
                        className="text-sm text-gray-900 flex items-start gap-2"
                      >
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>{msg}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {resolvedOpsMessages.length === 0 &&
                !hasFraudSignals &&
                legacyMessages.length === 0 &&
                isPlayerSubject && (
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">
                      No specific review triggers returned. Review metrics and fraud flags below.
                    </p>
                  </div>
                )}

              {showProfitMetrics && (
                <>
              {/* Key metrics */}
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  label="Profit Ratio"
                  icon={<TrendingUp className="h-4 w-4" />}
                  triggered={profit.ratioExceeded}
                  value={`${Number(profit.profitRatio || 0).toFixed(1)}x`}
                  subtext={
                    <>
                      Threshold: {profit.appliedThreshold}x
                    </>
                  }
                />
                <MetricCard
                  label="Net Profit"
                  icon={<DollarSign className="h-4 w-4" />}
                  triggered={profit.absoluteExceeded}
                  value={formatLedgerAmount(profit.netProfit, true)}
                  subtext={
                    profit.absoluteThresholdApplies === false ? (
                      <>Absolute USD threshold not applied ({amountLabel ?? 'credit ledger'})</>
                    ) : (
                      <>Threshold: {formatCurrency(profit.absoluteWinThreshold)}</>
                    )
                  }
                />
              </div>

              {/* Financial breakdown */}
              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Financial Breakdown ({profit.ledgerLabel || `${profit.lookbackPeriodDays}d`})
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Total {isCreditLedger ? 'Credit Inflows' : 'Deposits'}
                    </span>
                    <span className="font-medium">
                      {formatLedgerAmount(profit.totalDeposits)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Completed {isCreditLedger ? 'Credit Outflows' : 'Withdrawals'}
                    </span>
                    <span className="font-medium">
                      {formatLedgerAmount(profit.totalWithdrawals)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Pending {isCreditLedger ? 'Credit Outflows' : 'Withdrawals'}
                    </span>
                    <span className="font-medium">
                      {formatLedgerAmount(profit.pendingWithdrawals)}
                    </span>
                  </div>
                  <div
                    className={cn(
                      'flex justify-between border-t pt-2 rounded px-2 -mx-2',
                      profit.withdrawalThresholdExceeded
                        ? 'bg-red-50 border-red-100'
                        : ''
                    )}
                  >
                    <span className="text-muted-foreground font-medium flex items-center gap-2">
                      This Request
                      {profit.withdrawalThresholdExceeded && (
                        <Badge className="bg-red-600 text-white border-red-700 text-[10px]">
                          EXCEEDED
                        </Badge>
                      )}
                    </span>
                    <span
                      className={cn(
                        'font-bold',
                        profit.withdrawalThresholdExceeded
                          ? 'text-red-700'
                          : 'text-gray-800'
                      )}
                    >
                      {formatLedgerAmount(profit.currentRequestAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span className="text-muted-foreground">Total Withdrawal (incl. this request)</span>
                    <span>
                      {formatLedgerAmount(
                        analysis.totalWithdrawal ?? profit.totalWithdrawal ?? 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
                </>
              )}

              {/* User & hierarchy */}
              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {isPlayerSubject ? 'User Info' : `${ownerInfoLabel} Info`}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {isPlayerSubject ? 'Username' : 'Name'}
                    </span>
                    <span className="font-medium">
                      {orgOwnerPath ? (
                        <EntityLink to={orgOwnerPath}>{analysis.user.username}</EntityLink>
                      ) : playerPath ? (
                        <EntityLink to={playerPath}>{analysis.user.username}</EntityLink>
                      ) : (
                        analysis.user.username
                      )}
                    </span>
                  </div>
                  {isPlayerSubject && (
                  <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">
                      {analysis.user.email ? (
                        playerPath ? (
                          <EntityLink to={playerPath} className="break-all">
                            {analysis.user.email}
                          </EntityLink>
                        ) : (
                          analysis.user.email
                        )
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Account Age</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {analysis.playerAge ?? analysis.user.accountAgeDays} days
                      </span>
                      {profit.isNewAccount && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">
                          NEW
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Prior abnormal profit flags
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{relatedFlags}</span>
                      {relatedFlags > 0 && (
                        <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]">
                          REPEAT OFFENDER
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground pt-0.5">
                    Counts saved profit-fraud flags only (abnormal profit ratio, absolute profit,
                    new account high profit, repeated high profit). Manual amount reviews are not
                    included.
                  </p>
                  </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Registered</span>
                    <span className="font-medium">
                      {fmt(analysis.user.createdAt, { second: undefined })}
                    </span>
                  </div>
                </div>
              </div>

              {showHierarchy && (
                <div className="rounded-lg border p-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Hierarchy
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shop</span>
                      <span className="font-medium">
                        {shop ? (
                          <EntityLink to={`/shops/${encodeURIComponent(shop.id)}`}>
                            {shop.name}
                          </EntityLink>
                        ) : (
                          analysis.shopName ?? '—'
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Agent</span>
                      <span className="font-medium">
                        {agent ? (
                          <EntityLink to={`/agents/${encodeURIComponent(agent.id)}`}>
                            {agent.name}
                          </EntityLink>
                        ) : (
                          analysis.agentName ?? '—'
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Super Agent</span>
                      <span className="font-medium">
                        {superAgent ? (
                          <EntityLink
                            to={`/super-agents/${encodeURIComponent(superAgent.id)}`}
                          >
                            {superAgent.name}
                          </EntityLink>
                        ) : (
                          analysis.superAgentName ?? '—'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Fraud flags */}
              {analysis.flags.length > 0 && (
                <div className="rounded-lg border p-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Flag className="h-4 w-4" />
                    Fraud Flags ({analysis.flags.length})
                  </h4>
                  <div className="space-y-3">
                    {analysis.flags.map((flag) => (
                      <div
                        key={flag.id}
                        className="flex items-center justify-between rounded-md border p-3 bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <AlertOctagon
                            className={cn(
                              'h-5 w-5',
                              flag.severity === 'critical'
                                ? 'text-red-500'
                                : 'text-orange-500'
                            )}
                          />
                          <div>
                            <div className="text-sm font-medium">
                              {getFlagTypeLabel(flag.flagType)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {fmt(flag.createdAt)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(flag.severity)}>
                            {flag.severity.toUpperCase()}
                          </Badge>
                          {flag.isResolved ? (
                            <Badge className="bg-green-100 text-green-800 border-green-300">
                              Resolved
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                              Unresolved
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transaction */}
              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Transaction
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transaction ID</span>
                    <EntityLink
                      to={`/transactions?search=${encodeURIComponent(analysis.transaction.transactionId)}`}
                      className="font-mono text-xs break-all text-right max-w-[60%]"
                    >
                      {analysis.transaction.transactionId}
                    </EntityLink>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-bold text-gray-800">
                      {formatCurrency(analysis.transaction.amount)}
                    </span>
                  </div>
                  {analysis.transaction.withdrawalType && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium text-xs">
                        {analysis.transaction.withdrawalType}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                      {analysis.transaction.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span>
                      {fmt(analysis.transaction.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {withdrawal.status === 'awaiting_admin' && (
                <div className="flex gap-3 pt-2 border-t">
                  <Button
                    onClick={onApprove}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={onReject}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  {isPlayerSubject && (
                    <Button
                      variant="outline"
                      onClick={onAdjustBalance}
                      className="flex-1"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Adjust Balance
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfitAnalysisPanel;
